import { useTranslation } from "react-i18next";
import CenteredModal from "./CenteredModal";
import { ScissorsIcon, PlusIcon } from "@heroicons/react/outline";
import { MinusIcon } from "@heroicons/react/outline";
import { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import getCroppedImg from "../../helpers/cropImage";
import { classNames } from "../../helpers";
import UploaderButton from "../forms/UploaderButton";

type EditModalProps = {
  isOpen: boolean;
  onClose: () => void;
  image: File | null;
  onUpload: (file: File, status?: boolean) => void;
  isUploading: any;
};
export default function EditorModal(props: EditModalProps) {
  const { isOpen, onClose, image, onUpload, isUploading } = props;
  const LocalBasePath = "page.alert";
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [profilePictureURL, setProfilePictureURL] =
    useState<string | null>(null);
  const [fileImage, setFileImage] = useState<File | null>(null);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);
  const showCroppedImage = useCallback(async () => {
    try {
      const croppedImageInfo: {
        prevImageUrl: Promise<unknown>;
        fileImage: File;
      } | null = await getCroppedImg(
        profilePictureURL ?? "",
        croppedAreaPixels
      );
      croppedImageInfo?.prevImageUrl.then((preview: any) => {
        setProfilePictureURL(preview);
      });
      setZoom(1);
      if (!!croppedImageInfo?.fileImage)
        onUpload(croppedImageInfo?.fileImage!, true);
    } catch (e) {
      console.error(e);
    }
  }, [croppedAreaPixels, image]);
  const generateProfilePicture = async (file: File) => {
    setFileImage(file);
    let previewUrl: string | null = null;
    try {
      previewUrl = URL.createObjectURL(file);
      setProfilePictureURL(previewUrl);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (!!image) {
      generateProfilePicture(image);
    }
  }, [image]);

  return (
    <CenteredModal
      classStyles={"p-0"}
      setOpen={() => {
        setZoom(1);
        onClose();
      }}
      open={isOpen}
    >
      <div className="sm:max-w-[460px]">
        <div className="p-6 pb-0 mb-2">
          <div className="sm:flex sm:items-start">
            <div className="relative flex flex-col w-full gap-2">
              <div className="relative h-[275px] w-full bg-slate-200">
                <Cropper
                  image={profilePictureURL ?? ""}
                  crop={crop}
                  zoom={zoom}
                  maxZoom={3}
                  aspect={1}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                  cropShape="round"
                  showGrid={false}
                  objectFit="vertical-cover"
                />
              </div>
              <div className="flex items-center justify-between w-full gap-2">
                <button
                  className="border-none outline-none"
                  onClick={() => {
                    if (zoom < 3) {
                      setZoom((prev) => prev + 0.5);
                    }
                  }}
                >
                  <PlusIcon className="w-6 text-gray-700 cursor-pointer" />
                </button>
                <input
                  type="range"
                  name="zoom"
                  min={1}
                  max={3}
                  step={0.5}
                  value={zoom}
                  onChange={(e) => setZoom(+e.target.value)}
                  className="w-full h-0.5 bg-gray-300 appearance-none outline-none"
                />
                <button
                  className="border-none outline-none"
                  onClick={() => {
                    if (zoom > 1) {
                      setZoom((prev) => prev - 0.5);
                    }
                  }}
                >
                  <MinusIcon className="w-6 text-gray-300 cursor-pointer" />
                </button>
              </div>
              <div className="justify-center w-full gap-2 sm:flex">
                {/* <button
                  type="button"
                  className="inline-flex items-center justify-center w-full gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500 sm:mt-0 sm:w-44"
                  onClick={showCroppedImage}
                >
                  <ScissorsIcon className="w-5 text-gray-500" />
                  {t("cropImage")}
                </button> */}
                <UploaderButton
                  acceptFiles="image/*"
                  isUploading={isUploading}
                  onUpload={onUpload}
                  title={t("anotherImage")}
                />
              </div>
              <div className="w-full mt-4">
                <p className="w-full text-xs leading-5 text-gray-500">
                  {t("sharedImage")}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="gap-3 px-6 py-2 border-t border-gray-200 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            className="flex justify-center w-full px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm sm:w-auto bg-brandblue-600 hover:bg-brandblue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500"
            onClick={() => {
              showCroppedImage();
            }}
          >
            {t("save")}
          </button>
          <button
            type="button"
            className={classNames(
              "mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0  sm:w-auto",
              isUploading ? "disabled:opacity-75" : ""
            )}
            onClick={() => {
              onClose();
              setProfilePictureURL(null);
              setZoom(1);
            }}
          >
            {t("cancel")}
          </button>
        </div>
      </div>
    </CenteredModal>
  );
}
